import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Box,
  FormControl,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { getBanners, getRoles } from "redux/actions/Master";
import { useNavigate } from "react-router-dom";
import { AppInfoView, AppsPagination } from "@crema";
import AppsContainer from "@crema/core/AppsContainer";
import BannerTable from "./BannerTable";
import AppsContent from "@crema/core/AppsContainer/AppsContent";
import AppsHeader from "@crema/core/AppsContainer/AppsHeader";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { BannerData } from "types/models/master/MasterApp";
import { isModuleAuthorized } from "@crema/utility/Utils";

const bannerOptions = [
  { value: "All Banners", label: "All Banners" },
  { value: "homeBanner", label: "Home Banner" },
  { value: "homeBannerMobile", label: "Home Banner Mobile" },
  { value: "pricePlanPage", label: "Price Plan Page" },
  { value: "pricePlanPageMobile", label: "Price Plan Page Mobile" },
  { value: "pricePlanFooterBanner", label: "Price Plan Footer Banner" },
  { value: "pricePlanFooterBannerMobile", label: "Price Plan Footer Banner Mobile" },
];

interface MasterState {
  banners: BannerData[];
  bannerCount: number;
}

interface AppState {
  master: MasterState;
}

const Banners = () => {
  const { messages } = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rowsPerPage = 10;

  const [searchField, setSearchField] = useState("all");
  const [search, setSearchQuery] = useState<string>("");
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<string>("All Banners");

  const bannersState = useSelector((state: AppState) => state.master);
  const banners = bannersState.banners || [];

  const searchFields = [
    { label: "All", value: "all" },
    { label: "Manage Banner", value: "bannerType" },
    { label: "Title", value: "title" },
    { label: "Index No", value: "index" },
  ];

  useEffect(() => {
    dispatch(getRoles());
    dispatch(getBanners("", 0, 10000)); 
  }, [dispatch]);

  const onSearchBanner = (value: string) => {
    setSearchQuery(value);
    setPage(0);
  };

  const onPageChange = (
    event: React.ChangeEvent<unknown> | null,
    value: number
  ) => {
    setPage(value);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
    setPage(0);
  };

  const handleAddBannerClick = () => {
    navigate("/master/manageBanner/bannerCreate");
  };

  const filteredBanners = banners.filter((item) => {
    const searchTerm = search.trim().toLowerCase();
    const matchesFilter =
      filter === "All Banners" || item.bannerType === filter;

    if (!searchTerm) return matchesFilter;

    if (searchField === "all") {
      return (
        matchesFilter &&
        searchFields
          .filter((f) => f.value !== "all")
          .some(({ value }) => {
            const fieldValue = item[value];
            return fieldValue?.toString().toLowerCase().includes(searchTerm);
          })
      );
    } else {
      const fieldValue = item[searchField];
      return (
        matchesFilter &&
        fieldValue?.toString().toLowerCase().includes(searchTerm)
      );
    }
  });

  const paginatedBanners = filteredBanners.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );

  return (
    <>
      <AppsContainer
        title={
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: 16,
                fontWeight: "bold",
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {messages["sidebar.master.banners"]}
            </Box>

            {isModuleAuthorized("Add Banner") && (
              <Button
                variant="outlined"
                color="primary"
                onClick={handleAddBannerClick}
                sx={{ flexShrink: 0 }}
              >
                Add Banner
              </Button>
            )}
          </Box>
        }
        fullView
      >
        <AppsHeader>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControl
                fullWidth
                sx={{
                  width: 150,
                  height: "50px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <TextField
                  label="Manage Banner"
                  value={filter}
                  onChange={handleFilterChange}
                  select
                  fullWidth
                  sx={{
                    "& .MuiInputBase-input": {
                      height: "36px",
                      padding: "8px",
                    },
                  }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: "200px",
                        },
                      },
                    },
                  }}
                >
                  {bannerOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                justifyContent: "flex-end",
                width: 1,
              }}
            >
              <TextField
                select
                size="small"
                value={searchField}
                onChange={(e) => {
                  setSearchField(e.target.value);
                  setSearchQuery("");
                  setPage(0);
                }}
                SelectProps={{ native: true }}
              >
                {searchFields.map((field) => (
                  <option key={field.value} value={field.value}>
                    {field.label}
                  </option>
                ))}
              </TextField>
              <TextField
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => onSearchBanner(e.target.value)}
                placeholder={messages["common.searchHere"] as string}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {search ? (
                        <IconButton
                          onClick={() => onSearchBanner("")}
                          size="small"
                        >
                          <CloseIcon />
                        </IconButton>
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 200 }}
              />
            </Box>
          </Box>
        </AppsHeader>

        <AppsContent
          sx={{
            paddingTop: 2.5,
            paddingBottom: 2.5,
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <BannerTable banners={paginatedBanners} />
          <AppsPagination
            rowsPerPage={rowsPerPage}
            count={filteredBanners.length}
            page={page}
            onPageChange={onPageChange}
          />
        </AppsContent>
      </AppsContainer>

      <AppInfoView />
    </>
  );
};

export default Banners;
